import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { appointmentPublicApi, type PublicBookingResponse } from '@/components/appointment-widget/api';
import '@/components/appointment-widget/widget.css';

/**
 * Public page where the customer manages their booking via a signed cancel/reschedule
 * token sent in the confirmation email. The backend token endpoint returns only
 * a flat snapshot — slug, service id and consultant id are intentionally NOT
 * exposed — so reschedule uses a free datetime input (which the backend
 * re-validates against the consultant's calendar).
 */
export default function BookingManage() {
  const { cancelToken } = useParams<{ cancelToken: string }>();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState<PublicBookingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'cancel' | 'reschedule' | 'cancelled' | 'rescheduled'>(
    searchParams.get('action') === 'reschedule' ? 'reschedule' : 'view',
  );
  const [busy, setBusy] = useState(false);
  const [newStart, setNewStart] = useState('');

  useEffect(() => {
    if (!cancelToken) {
      setError('Token prenotazione mancante');
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    setBooking(null);
    appointmentPublicApi
      .getBooking(cancelToken)
      .then((b) => {
        if (!active) return;
        setBooking(b);
        setMode(b.status === 'cancelled' ? 'view'
          : searchParams.get('action') === 'reschedule' ? 'reschedule' : 'view');
        if (b?.branding) {
          const root = document.documentElement;
          if (b.branding.primaryColor) root.style.setProperty('--aw-primary', b.branding.primaryColor);
          if (b.branding.accentColor) root.style.setProperty('--aw-accent', b.branding.accentColor);
        }
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : 'Errore caricamento prenotazione');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cancelToken, searchParams]);

  const handleCancel = async () => {
    if (!cancelToken) return;
    setBusy(true);
    setError(null);
    try {
      await appointmentPublicApi.cancelBooking(cancelToken);
      setBooking((current) => current ? { ...current, status: 'cancelled' } : current);
      setMode('cancelled');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore annullamento');
    } finally {
      setBusy(false);
    }
  };

  const handleReschedule = async () => {
    if (!cancelToken || !newStart) return;
    setBusy(true);
    setError(null);
    try {
      // <input type="datetime-local"> emits "YYYY-MM-DDTHH:mm" without seconds/TZ;
      // convert to a real ISO string in the user's local time zone for the backend.
      const iso = new Date(newStart).toISOString();
      await appointmentPublicApi.rescheduleBooking(cancelToken, iso);
      setBooking((current) => {
        if (!current) return current;
        const duration = new Date(current.endTime).getTime() - new Date(current.startTime).getTime();
        return {
          ...current,
          startTime: iso,
          endTime: new Date(new Date(iso).getTime() + duration).toISOString(),
          status: 'rescheduled',
        };
      });
      setMode('rescheduled');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile riprogrammare l’appuntamento');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loader2 className="aw-spin" size={32} />
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="aw-root" style={{ maxWidth: 480, margin: '40px auto' }}>
        <div className="aw-shell">
          <div className="aw-error">{error}</div>
          <p style={{ color: 'var(--aw-muted)' }}>Il link potrebbe essere scaduto o non valido.</p>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const start = new Date(booking.startTime);
  const isCancelled = booking.status === 'cancelled';

  return (
    <div className="aw-root" style={{ maxWidth: 560, margin: '24px auto', minHeight: '100vh' }}>
      <div className="aw-shell">
        <h1 style={{ fontSize: 22, margin: '0 0 6px' }}>La tua prenotazione</h1>
        <p style={{ color: 'var(--aw-muted)', margin: '0 0 20px', fontSize: 14 }}>{booking.formName}</p>

        {error && <div className="aw-error">{error}</div>}

        <div className="aw-card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CalendarIcon size={18} style={{ color: 'var(--aw-primary)' }} />
            <strong>
              {start.toLocaleDateString('it-IT', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })}
            </strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Clock size={18} style={{ color: 'var(--aw-primary)' }} />
            <strong>{start.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</strong>
          </div>
          {booking.typeName && (
            <p style={{ marginTop: 6, color: 'var(--aw-muted)' }}>{booking.typeName}</p>
          )}
          {isCancelled && (
            <p style={{ marginTop: 8, color: '#ef4444', fontWeight: 600 }}>Stato: annullato</p>
          )}
        </div>

        {mode === 'view' && !isCancelled && (
          <div className="aw-actions">
            <button className="aw-btn aw-btn-ghost" onClick={() => setMode('cancel')}>Annulla</button>
            <button className="aw-btn aw-btn-primary" onClick={() => setMode('reschedule')}>Riprogramma</button>
          </div>
        )}

        {mode === 'cancel' && (
          <div>
            <h3 style={{ fontSize: 16, margin: '0 0 8px' }}>Vuoi annullare l'appuntamento?</h3>
            <p style={{ color: 'var(--aw-muted)', fontSize: 13, marginTop: 0 }}>
              Puoi annullare fino a {booking.cancelDeadlineHours}h prima dell'appuntamento.
            </p>
            <div className="aw-actions aw-actions-sticky">
              <button className="aw-btn aw-btn-ghost" onClick={() => setMode('view')} disabled={busy}>
                Indietro
              </button>
              <button
                className="aw-btn aw-btn-primary"
                onClick={handleCancel}
                disabled={busy}
                style={{ background: '#ef4444', borderColor: '#ef4444' }}
              >
                {busy ? <Loader2 className="aw-spin" size={16} /> : 'Conferma annullamento'}
              </button>
            </div>
          </div>
        )}

        {mode === 'reschedule' && (
          <div>
            <h3 style={{ fontSize: 16, margin: '0 0 8px' }}>Scegli un nuovo orario</h3>
            <p style={{ color: 'var(--aw-muted)', fontSize: 13, marginTop: 0 }}>
              Puoi modificare l'appuntamento fino a {booking.rescheduleDeadlineHours}h prima dell'inizio.
              La disponibilità viene verificata al momento della conferma.
            </p>
            <input
              type="datetime-local"
              aria-label="Nuovo orario"
              className="aw-input"
              value={newStart}
              min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
              onChange={(e) => setNewStart(e.target.value)}
              style={{ marginTop: 8 }}
            />
            <div className="aw-actions aw-actions-sticky">
              <button className="aw-btn aw-btn-ghost" onClick={() => setMode('view')} disabled={busy}>
                Indietro
              </button>
              <button
                className="aw-btn aw-btn-primary"
                onClick={handleReschedule}
                disabled={!newStart || busy}
              >
                {busy ? <Loader2 className="aw-spin" size={16} /> : 'Conferma nuovo orario'}
              </button>
            </div>
          </div>
        )}

        {mode === 'cancelled' && (
          <div className="aw-confirm">
            <div className="aw-confirm-icon"><XCircle size={32} /></div>
            <h3>Appuntamento annullato</h3>
            <p>La tua prenotazione è stata cancellata correttamente.</p>
          </div>
        )}

        {mode === 'rescheduled' && (
          <div className="aw-confirm">
            <div className="aw-confirm-icon"><CheckCircle2 size={32} /></div>
            <h3>Appuntamento riprogrammato</h3>
            <p>Il nuovo orario è stato salvato. Le comunicazioni seguono le impostazioni della prenotazione.</p>
          </div>
        )}
      </div>
    </div>
  );
}
